import { SuperLink as Link } from "@/components/common/super-link";

const intro = (
  <p>
    <strong>Wyszukiwarka ICD-10</strong> służy do odnalezienia kodów w systemie{" "}
    <strong>ICD-10</strong>. <strong>Kodowanie ICD-10</strong> jest niezbędnym
    elementem wypełnianej przez lekarza dokumentacji medycznej pacjenta. Aby
    wyszukać odpowiedni <strong>kod ICD-10</strong>, należy wprowadzić nazwę
    jednostki chorobowej lub jej fragment w pole wyszukiwarki. Przechodząc do
    wybranego kodu ICD-10, użytkownik uzyskuje dostęp dodatkowych materiałów:{" "}
    <Link
      href="https://remedium.md/poradniki/"
      target="_blank"
      rel="noreferrer"
    >
      Poradników medycznych
    </Link>{" "}
    oraz
    <a
      href="https://remedium.md/wyszukiwarka-lekow/"
      target="_blank"
      rel="noreferrer"
    >
      Wyszukiwarki Leków
    </a>
    .
  </p>
);

const content = (
  <>
    <h3>ICD 10 - co to jest?</h3>
    <p>
      <strong>ICD-10</strong> (z ang. International Statistical Classification
      of Diseases and Related Health Problems, 10th Revision) to Międzynarodowa
      Klasyfikacja Chorób i Problemów Zdrowotnych opracowana przez Światową
      Organizację Zdrowia (WHO). ICD-10 zastąpił ICD-9. Klasyfikacja zawiera
      kompletne spektrum stanów chorobowych. Każda jednostka chorobowa, stan lub
      uraz posiadają odrębny kod, którym posługuje się lekarz stawiający
      diagnozę, wypełniając dokumentację medyczną pacjenta. Zaklasyfikowane w
      ten sposób dane ułatwiają spójny przekaz informacji medycznej na całym
      świecie. Aby odnaleźć odpowiedni <strong>kod ICD-10</strong>, skorzystaj z
      przeznaczonej do tego <strong>Wyszukiwarki ICD-10</strong>.
    </p>

    <h3>Kody ICD 10</h3>
    <p>
      <strong>Kody ICD-10</strong> składają się z litery oraz z trzech cyfr i
      wykorzystywane się w <strong>klasyfikacji ICD-10</strong>. Kody ICD są
      ujednolicone i wykorzystywane na całym świecie, służą zaklasyfikowaniu
      objawów występujących u pacjenta do odpowiadającej jednostki chorobowej,
      stanu lub urazu. Litera oznacza grupę chorób - np. litera A - Wybrane
      choroby zakaźne i pasożytnicze; cyfry zaś jednostkę chorobową znajdującą
      się w tej grupie - np. A08 Wirusowe i inne określone zakażenia jelit. Kody
      ICD-10 można odnaleźć za pomocą <strong>Wyszukiwarki ICD-10</strong>,
      wprowadzając nazwę jednostki chorobowej lub jej fragment w pole
      wyszukiwarki.
    </p>

    <h3>Najpopularniejsze kody ICD-10 w POZ</h3>
    <p>
      Na pełną <strong>klasyfikację ICD-10</strong> składa się ponad 14 tysięcy
      unikatowych <strong>kodów ICD</strong>. Zazwyczaj w praktyce klinicznej
      lekarz spotyka się tylko z grupą kodów dotyczących stanów chorobowych lub
      urazów, które występują u pacjentów najczęściej. Znajomość
      najpopularniejszych kodów ICD-10 stanowi obok{" "}
      <strong>Wyszukiwarki ICD-10</strong> użyteczne narzędzie w praktyce
      lekarza POZ. Do najczęściej używanych kodów ICD-10 należą:
    </p>
    <ul>
      <li>
        <a
          href="https://remedium.md/icd10/wybrane-choroby-zakazne-i-pasozytnicze/biegunka-i-zapalenie-zoladkowo-jelitowe-o-przypuszczalnie-zakaznej-etiologii"
          target="_blank"
          rel="noreferrer"
        >
          A 09 Biegunka i zapalenie żołądkowo-jelitowe o przypuszczalnie
          zakaźnej etiologii
        </a>
        ,
      </li>
      <li>
        <a
          target="_blank"
          href="https://remedium.md/icd10/objawy-cechy-chorobowe-oraz-nieprawidlowe-wyniki-badan-klinicznych-i-laboratoryjnych-niesklasyfikowane-gdzie-indziej/bol-glowy"
          rel="noreferrer"
        >
          R51 Ból głowy
        </a>
      </li>
      <li>
        <a
          target="_blank"
          href="https://remedium.md/icd10/choroby-ukladu-oddechowego/naczynioruchowy-i-alergiczny-niezyt-nosa"
          rel="noreferrer"
        >
          J30 Naczynioruchowy i alergiczny nieżyt nosa
        </a>
      </li>
      <li>
        <a
          target="_blank"
          href="https://remedium.md/icd10/objawy-cechy-chorobowe-oraz-nieprawidlowe-wyniki-badan-klinicznych-i-laboratoryjnych-niesklasyfikowane-gdzie-indziej/bol-w-okolicy-brzucha-i-miednicy"
          rel="noreferrer"
        >
          R10 Ból w okolicy brzucha i miednicy
        </a>
      </li>
    </ul>

    <p>
      O najpopularniejszych kodach ICD-10 w POZ szerzej piszemy w naszym e-booku{" "}
      <a
        target="_blank"
        href="https://remedium.md/ebook/pierwsze-kroki-w-poz"
        rel="noreferrer"
      >
        Pierwsze kroki w POZ (Edycja 2022).
      </a>
    </p>

    <h3>ICD-10 vs. ICD-11</h3>
    <p>
      W 2018 roku Światowa Organizacja Zdrowia (WHO) opracowała i wydała nową
      klasyfikację <strong>ICD-11</strong>. Zawierająca ponad 17 000 unikalnych
      kodów i ponad 120 000 terminów nowa klasyfikacja ICD-11 miała na początku
      2022 roku zastąpić aktualnie obowiązującą w Polsce klasyfikację ICD-10. Do
      najważniejszych zmian w <strong>ICD-11 w stosunku do ICD-10</strong>{" "}
      należą:
    </p>
    <ul>
      <li>udostępniono klasyfikację ICD-11 w formie elektronicznej,</li>
      <li>
        przebudowano klasyfikację zaburzeń z obszaru zdrowia psychicznego,
      </li>
      <li>
        dodano kody odporności na środki przeciwdrobnoustrojowe zgodnie z
        raportem GLASS-AMR,
      </li>
      <li>dodano kodowanie uwzględniające stany klinicznie HIV,</li>
      <li>poszerzono kodowanie dotyczące powikłań cukrzycy,</li>
      <li>
        wprowadzono kody uwzględniające podtypy raka podstawnego i czerniaka
        skóry, klasyfikację chorób zastawek serca i nadciśnienia płucnego,
      </li>
      <li>
        kodowanie wypadków drogowych i przyczyn obrażeń zgodne z obecną
        międzynarodową praktyką.
      </li>
    </ul>

    <p>
      Zgodnie z informacją opublikowaną na stronie Centrum E-zdrowia, termin
      wdrażania <strong>ICD-11</strong> w Polsce planowany jest na termin 30
      czerwca 2023 roku.
    </p>
  </>
);

export const ICD10Description = { intro, content };
